"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = getProductConfiguration;
var _functions = require("../utils/functions");
var _appConfig = require("../config/app-config");
/* eslint-disable max-len, no-undefined */

class ProductConfigurationFormatter {
  constructor({
    data,
    environment,
    content,
    instance
  }) {
    this.data = data;
    this.content = content;
    this.instance = instance;
    this.environment = environment;
    this.market = environment.market || _appConfig.DEFAULT_VALUES.market;
    this.currency = environment.currency || _appConfig.DEFAULT_VALUES.currency;

    /**
     * Maps display types to handler methods to avoid switch statements
     * in the processing loop
     */
    this.DISPLAY_HANDLERS = {
      termSelector: this.getTermSelector.bind(this),
      quantitySelector: this.getQuantitySelector.bind(this),
      pillSelector: this.getPillSelector.bind(this),
      toggle: this.getToggle.bind(this),
      featureBanner: this.getFeatureBanner.bind(this)
    };
  }
  getRowTitle(displayArea, productData, productAppliedTitles) {
    const titleKey = `${displayArea}RowTitle`;
    if (!productAppliedTitles[titleKey] && productData.content[titleKey]) {
      productAppliedTitles[titleKey] = true;
      return productData.content[titleKey];
    }
    return null;
  }
  getTermOptionPriceDescription(isDomain, priceData, priceDescription, termTotalPrice) {
    const {
      salepriceformatted,
      listpriceformatted
    } = priceData;
    if (isDomain) {
      // For domains: use listpriceformatted as total price (no /yr suffix) if available and different from sale price
      return listpriceformatted && listpriceformatted !== salepriceformatted ? listpriceformatted : '';
    }
    // For non-domains: use existing priceDescription logic
    return priceDescription && priceDescription.includes('{totalPrice}') ? priceDescription.replace('{totalPrice}', termTotalPrice).trim() : '';
  }
  getTermSavingsTag(percentagesavings) {
    return percentagesavings && percentagesavings !== 0 ? this.content?.savePercentage?.replace('{value}', percentagesavings) : '';
  }
  getTermLengthDisplay(content, enumVal, displayEnumVal, termTypeText) {
    return content[enumVal]?.title?.includes('{termType}') ? `${displayEnumVal} ${termTypeText}` : content[enumVal]?.title || `${displayEnumVal} ${termTypeText}`;
  }
  getTermSelector(product, productData) {
    const {
      term: {
        content,
        attributes
      },
      prices = {}
    } = productData;
    const {
      enum: enums,
      termType,
      display,
      default: defaultTerm
    } = attributes;

    // Check if this is a domain product
    const isDomain = this.instance[product]?.properties?.productType === 'domain';
    const {
      priceDescription
    } = content.shared;
    const termPath = `${product}/properties/term`;
    const termID = `id-${termPath}`;
    const termIndex = enums.indexOf(parseInt(defaultTerm, 10));
    const itemOptions = enums.map(enumVal => {
      const optionId = `${termID}.numberOfTerms.${enumVal}`;
      const priceData = prices[enumVal] || {};
      const {
        sale,
        percentagesavings,
        salepriceformatted
      } = priceData;
      const termTotalPrice = sale || sale === 0 ? (0, _functions.formatMicrounits)(sale * enumVal, this.market, this.currency) : undefined;
      const optionPriceDescription = this.getTermOptionPriceDescription(isDomain, priceData, priceDescription, termTotalPrice);

      // For domains, convert months to years for display
      const displayTermType = isDomain ? 'YEAR' : termType;
      const displayEnumVal = isDomain ? enumVal / 12 : enumVal;

      // For domains, we need to use the displayEnumVal for term text
      const termTypeText = (0, _functions.getTermTypeText)(displayTermType, this.content, displayEnumVal);
      return {
        id: optionId,
        _name: optionId.replace('.', '_'),
        savingsTag: this.getTermSavingsTag(percentagesavings),
        termLength: this.getTermLengthDisplay(content, enumVal, displayEnumVal, termTypeText),
        termPrice: isDomain ? salepriceformatted : `${salepriceformatted}${(0, _functions.getTermPriceText)(displayTermType, this.content)}`,
        termTotalPrice: optionPriceDescription,
        numberOfTerm: enumVal,
        instancePath: termPath,
        isDomain // Pass the isDomain flag to the component
      };
    });
    const termSelector = {
      ...content.shared,
      id: termID,
      layouts: display,
      defaultSelected: termIndex,
      items: [{
        ariaLabel: display || 'select-term-dropdown',
        options: itemOptions
      }]
    };
    return {
      termSelector
    };
  }
  getQuantitySelector(product, productData) {
    const quantityPath = `${product}/properties/quantity`;
    if (!productData?.pkgQty) return null;
    const {
      pkgQty: {
        content,
        attributes: {
          display,
          displayDisable
        }
      }
    } = productData;
    const quantitySelector = {
      ...content.shared,
      instancePath: quantityPath,
      id: `id-${quantityPath}`,
      layouts: display,
      displayDisable
    };
    return {
      quantitySelector
    };
  }

  // eslint-disable-next-line max-statements
  getToggle(product, productData, {
    itemData,
    itemName
  }, productAppliedTitles) {
    const {
      default: defaultTerm,
      termType
    } = productData.term.attributes;
    const {
      content,
      prices = {},
      attributes: {
        display,
        displayArea,
        displayDisable,
        enum: enums,
        fixedNumberOfTerms
      }
    } = itemData;
    const togglePath = `${product}/${displayArea}/${itemName}`;
    const {
      priceOverride,
      priceDescription,
      perMonthOverride,
      rowTitle
    } = content.shared;
    const priceData = prices[defaultTerm] ?? prices[enums[0]] ?? {};
    const {
      salepriceformatted,
      sale,
      percentagesavings
    } = priceData;
    const quantity = (0, _functions.getProductQuantity)(this.instance, product);
    const termLength = fixedNumberOfTerms || defaultTerm;
    const isPillToggle = enums.length > 1;
    const currentSelection = isPillToggle ? this.instance?.[product]?.[displayArea]?.[itemName]?.selection : null;
    let items = [];
    const togglePrice = (0, _functions.calculateTotalPrice)(sale, termLength, quantity, this.market, this.currency);
    if (isPillToggle) {
      const defaultEnum = enums[0];
      const defaultEnumContent = {
        ...content[defaultEnum]
      };
      delete defaultEnumContent.list;
      const pillToggleItem = {
        ...content.shared,
        ...defaultEnumContent,
        title: content.shared.title,
        productTagLabel: defaultEnumContent.tagLabel,
        instancePath: togglePath,
        id: defaultEnum,
        _name: defaultEnum,
        tag: this.content.startingAt,
        price: (0, _functions.getPriceDisplay)(priceOverride, salepriceformatted, termType, this.content, perMonthOverride),
        priceDescription: (0, _functions.getPriceDescription)(togglePrice, priceDescription),
        displayDisable,
        isPillToggle: true,
        options: enums.map(enumVal => {
          const optionData = prices[enumVal] || prices[defaultTerm] || {};
          const {
            salepriceformatted: optionPriceFormatted,
            sale: optionSale
          } = optionData;
          const individualOptionPrice = (0, _functions.calculateTotalPrice)(optionSale, termLength, quantity, this.market, this.currency);
          return {
            ...content[enumVal],
            productTagLabel: content[enumVal].tagLabel,
            id: enumVal,
            _name: enumVal,
            value: enumVal,
            tag: (0, _functions.getSavingsTag)(optionData.percentagesavings, this.content.savePercentage),
            price: (0, _functions.getPriceDisplay)(content[enumVal].priceOverride, salepriceformatted, termType, this.content, perMonthOverride, optionPriceFormatted),
            priceDescription: (0, _functions.getPriceDescription)(individualOptionPrice, content[enumVal].priceDescription, priceDescription),
            ...(content[enumVal]?.list && {
              list: content[enumVal].list
            })
          };
        })
      };
      items = [pillToggleItem];
    } else {
      items = enums.map(enumVal => {
        const enumPriceDescription = content[enumVal].priceDescription || priceDescription;
        const {
          tagLabel: productTagLabel
        } = content[enumVal];
        return {
          ...content.shared,
          ...content[enumVal],
          productTagLabel,
          instancePath: togglePath,
          id: enumVal,
          _name: enumVal,
          tag: (0, _functions.getSavingsTag)(percentagesavings, this.content.savePercentage),
          price: (0, _functions.getPriceDisplay)(priceOverride, salepriceformatted, termType, this.content, perMonthOverride),
          priceDescription: (0, _functions.getPriceDescription)(togglePrice, enumPriceDescription),
          displayDisable
        };
      });
    }
    const selectorRowTitle = this.getRowTitle(displayArea, productData, productAppliedTitles) || rowTitle;
    const attachmentSelector = {
      layouts: display,
      rowTitle: selectorRowTitle,
      items,
      selectedOption: currentSelection ? enums.indexOf(currentSelection) : 0
    };
    return {
      attachmentSelector
    };
  }
  getPillSelector(product, productData, {
    itemData,
    itemName
  }, productAppliedTitles) {
    const {
      default: defaultTerm,
      termType
    } = productData.term.attributes;
    const {
      content,
      prices = {},
      attributes: {
        display,
        displayArea,
        split,
        enum: enums,
        fixedNumberOfTerms
      }
    } = itemData;
    const contentPath = `${product}/${displayArea}/${itemName}`;
    const {
      rowTitle,
      priceDescription,
      perMonthOverride
    } = content.shared;
    const priceData = prices[defaultTerm] ?? prices[enums[0]] ?? {};
    const {
      salepriceformatted
    } = priceData;
    const quantity = (0, _functions.getProductQuantity)(this.instance, product);
    const termLength = fixedNumberOfTerms || defaultTerm;
    const shouldShowPricing = displayArea === 'addons';
    const selectorRowTitle = this.getRowTitle(displayArea, productData, productAppliedTitles) || rowTitle;
    const options = enums.map(enumVal => {
      const {
        title,
        description,
        tagLabel: productTagLabel,
        list
      } = content[enumVal];
      const baseOption = {
        title: title || 'Product Name',
        productTagLabel,
        description,
        id: enumVal,
        instancePath: `${contentPath}/${enumVal}`,
        ...(list && {
          list
        })
      };

      // Add pricing information only when displayArea is 'addons'
      if (shouldShowPricing && enumVal !== 'none') {
        const optionData = prices[enumVal] || prices[defaultTerm] || {};
        const {
          salepriceformatted: optionPriceFormatted,
          sale: optionSale,
          percentagesavings
        } = optionData;
        const individualOptionPrice = (0, _functions.calculateTotalPrice)(optionSale, termLength, quantity, this.market, this.currency);
        baseOption.tag = (0, _functions.getSavingsTag)(percentagesavings, this.content.savePercentage);
        baseOption.price = (0, _functions.getPriceDisplay)(content[enumVal].priceOverride, salepriceformatted, termType, this.content, perMonthOverride, optionPriceFormatted);
        baseOption.priceDescription = (0, _functions.getPriceDescription)(individualOptionPrice, content[enumVal].priceDescription, priceDescription);
      }
      return baseOption;
    });
    const {
      tagLabel: sharedTagLabel,
      ...sharedContent
    } = content.shared;
    const optionSelector = {
      ...sharedContent,
      ...itemData,
      sharedTagLabel,
      rowTitle: selectorRowTitle,
      instancePath: contentPath,
      id: `id-${contentPath}`,
      layouts: display,
      items: [{
        split,
        options
      }]
    };
    return {
      optionSelector
    };
  }
  getFeatureBanner(product, productData, {
    itemData,
    itemName
  }, productAppliedTitles) {
    const {
      content,
      attributes: {
        display,
        displayArea
      }
    } = itemData;
    const addonPath = `${product}/${displayArea}/${itemName}`;
    const {
      rowTitle
    } = content.shared;
    const selectorRowTitle = this.getRowTitle(displayArea, productData, productAppliedTitles) || rowTitle;
    const featureBanner = {
      ...content.shared,
      rowTitle: selectorRowTitle,
      instancePath: addonPath,
      id: `id-${addonPath}`,
      layouts: display
    };
    return {
      featureBanner
    };
  }
  processItem(product, productData, item, productAppliedTitles) {
    const display = item.itemData.attributes.display;
    const handler = this.DISPLAY_HANDLERS[display];
    if (handler) {
      return handler(product, productData, item, productAppliedTitles);
    }
    return null;
  }
  generate() {
    const productConfiguration = {};
    Object.entries(this.data).forEach(([product, productData]) => {
      const productAppliedTitles = {
        includesRowTitle: false,
        addonsRowTitle: false,
        offersRowTitle: false,
        bannersRowTitle: false
      };
      const itemRows = [];
      Object.entries(productData).forEach(([groupName, groupData]) => Object.entries(groupData).forEach(([contentName, contentData]) => {
        let item;
        if (contentData?.display) {
          item = {
            itemName: groupName,
            itemData: groupData
          };
        } else if (contentData?.attributes?.display) {
          item = {
            itemName: contentName,
            itemData: contentData
          };
        } else {
          return;
        }
        const result = this.processItem(product, productData, item, productAppliedTitles);
        if (result) {
          itemRows.push(result);
        }
      }));
      productConfiguration[product] = {
        itemRows
      };
    });
    return productConfiguration;
  }
}
function getProductConfiguration(responseData = {}) {
  const formatter = new ProductConfigurationFormatter(responseData);
  return formatter.generate();
}